import {
  createContext,
  forwardRef,
  useContext,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';

import { cn } from './cn';

interface RadioGroupContextValue {
  name: string;
  value?: string;
  onChange?: (value: string) => void;
}

const RadioGroupContext = createContext<RadioGroupContextValue | null>(null);

export interface RadioGroupProps {
  name?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  children: ReactNode;
  className?: string;
  ariaLabel?: string;
}

export function RadioGroup({
  name,
  value,
  onChange,
  children,
  className,
  ariaLabel,
}: RadioGroupProps): JSX.Element {
  const fallbackName = useId();
  return (
    <div role="radiogroup" aria-label={ariaLabel} className={cn('flex flex-col gap-2', className)}>
      <RadioGroupContext.Provider value={{ name: name ?? fallbackName, value, onChange }}>
        {children}
      </RadioGroupContext.Provider>
    </div>
  );
}

export interface RadioProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  value: string;
  label?: ReactNode;
  description?: ReactNode;
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>(function Radio(
  { value, label, description, className, id, ...rest },
  ref,
) {
  const group = useContext(RadioGroupContext);
  const radioId = id ?? `${group?.name ?? 'radio'}-${value}`;
  return (
    <label htmlFor={radioId} className="flex items-start gap-2 cursor-pointer select-none">
      <input
        ref={ref}
        id={radioId}
        type="radio"
        name={group?.name ?? rest.name}
        value={value}
        checked={group ? group.value === value : undefined}
        onChange={() => group?.onChange?.(value)}
        className={cn(
          'mt-0.5 h-4 w-4 border-border text-primary',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...rest}
      />
      {(label || description) && (
        <span className="flex flex-col">
          {label && <span className="text-sm text-foreground">{label}</span>}
          {description && <span className="text-xs text-muted">{description}</span>}
        </span>
      )}
    </label>
  );
});
